import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  OutlinedInput,
  Chip,
  Autocomplete,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Backdrop,
  GlobalStyles,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import QuickTimeEntryForm from './Forms/QuickTimeEntryForm';
import EditTimeEntryForm from './Forms/EditTimeEntryForm';
import TimeEntryDetails from './TimeEntryDetails';

import { useTeamspace } from '../context/TeamspaceProvider';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { zonedTimeToUtc, utcToZonedTime, format } from 'date-fns-tz';
import { subDays } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { escapeHtml } from '../utils/emailBody';
import { useGridPresence } from '../hooks/useGridPresence';
import CalendarPresenceMarkers from './Presence/CalendarPresenceMarkers';
import useIsPhone, { PHONE_MEDIA, SHORT_TOUCH_MEDIA } from '../hooks/useIsPhone';
import { weekAnchorDate } from '../utils/calendarRange';

// Phones get Day / 3 Days / Week. The 3-day view is a custom timeGrid; registering
// it is harmless on desktop, which only ever shows timeGridWeek.
const CALENDAR_VIEWS = { timeGridThreeDay: { type: 'timeGrid', duration: { days: 3 } } };
const PHONE_VIEW_OPTIONS = [
  { value: 'timeGridDay', label: 'Day' },
  { value: 'timeGridThreeDay', label: '3 Days' },
  { value: 'timeGridWeek', label: 'Week' },
];
// Touch: FullCalendar waits 1s before a press-and-hold selects a slot.
const PHONE_LONG_PRESS_MS = 350;

// Phones: the filter controls fold away behind a "Filters" button. Desktop renders
// the children straight into the filter bar, exactly as before.
const PhoneFilterCollapse = ({ isPhone, open, children }) => (isPhone ? (
  <Collapse in={open} sx={{ width: '100%' }} unmountOnExit>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
      {children}
    </Box>
  </Collapse>
) : <>{children}</>);

dayjs.extend(utc);
dayjs.extend(timezone);

// Builds each event block's HTML for FullCalendar. It reads nothing but its argument, so it
// lives at module scope and keeps one identity across renders — an inline function is a
// changed option on every render. The body is moved verbatim: whitespace inside its template
// literals is part of the HTML it returns.
const renderEventHtml = (arg) => {
            const entry = arg.event.extendedProps?.entry;
            if (!entry) return { html: '<div></div>' };
        
            // Change threshold to 20 mins or less for the "ultra-compact" one-line view
            const durationMs = arg.event.end && arg.event.start ? arg.event.end.getTime() - arg.event.start.getTime() : 0;
            const isVeryShort = durationMs <= (20 * 60 * 1000); // 20 mins or less
            const isShort = durationMs < (60 * 60 * 1000); // Less than 1 hour
            const isLongEnough = durationMs >= (35 * 60 * 1000); // 35 mins or more
        
            const calculateDurationFromEventTimes = () => {
                if (!arg.event.start || !arg.event.end) return entry.duration || 0;
                return Math.floor(durationMs / 1000);
            };
        
            const formatDuration = (seconds) => {
                if (!seconds || seconds === 0) return '';
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            };
        
            const eventDuration = calculateDurationFromEventTimes();
            const duration = formatDuration(eventDuration);
            // Escaped: FullCalendar inserts this string as raw HTML, so user text must not carry markup.
            const projectName = escapeHtml(arg.event.extendedProps.project || '');
            const description = escapeHtml(entry.description || '');
        
            // Billable Icon Logic
            const isBillable = entry.billable !== false;
            const billableIconHtml = isBillable ? `<svg style="width: 10px; height: 10px; flex-shrink: 0; margin-left: 2px;" viewBox="0 0 24 24" fill="#FFD700"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.16-3.4-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>` : '';
        
            if (isVeryShort) {
                // ONE LINE LAYOUT (Duration + Description + Project) - All details on one line, truncate if too long
                // Duration: bold, Description: bold, Project: plain and smaller
                const descriptionSpan = description ? `<span style="font-weight: bold;">${description}</span>` : '';
                const projectSpan = projectName ? `<span style="font-size: 0.85em; margin-left: 4px;">${projectName}</span>` : '';
                const contentText = `${descriptionSpan}${projectSpan}`.trim();
                return {
                    html: `
                    <div style="display: flex; align-items: center; width: 100%; height: 100%; padding: 0 4px; overflow: hidden; white-space: nowrap; box-sizing: border-box; font-size: 9px; color: inherit; min-width: 0;">
                        <span style="font-weight: bold; flex-shrink: 0; margin-right: 4px; white-space: nowrap;">${duration}</span>
                        <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1 1 auto; min-width: 0;">
                            ${contentText}
                        </span>
                        ${billableIconHtml ? `<span style="flex-shrink: 0; margin-left: 2px; white-space: nowrap;">${billableIconHtml}</span>` : ''}
                    </div>
                    `
                };
            }
        
            // Standard Multi-line Layout for larger blocks
            // Duration: bold, Description: bold, Project: plain and smaller
            const durationHtml = duration ? `<div style="font-size: 8px; font-weight: bold;">${duration}</div>` : '';
            
            let descriptionHtml = '';
            let projectHtml = '';
            
            if (isLongEnough) {
                // For entries 35 minutes or more: project on separate line below description
                const descriptionSpan = description ? `<span style="font-weight: bold;">${description}</span>` : '';
                descriptionHtml = description ? `<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${descriptionSpan}</div>` : '';
                const projectSpan = projectName ? `<span style="font-size: 0.85em;">${projectName}</span>` : '';
                projectHtml = projectName ? `<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${projectSpan}</div>` : '';
            } else {
                // For entries less than 35 minutes: description and project on same line
                const descriptionSpan = description ? `<span style="font-weight: bold;">${description}</span>` : '';
                const projectSpan = projectName ? `<span style="font-size: 0.85em; margin-left: 4px;">${projectName}</span>` : '';
                descriptionHtml = description || projectName ? `<div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${descriptionSpan}${projectSpan}</div>` : '';
            }
        
            return {
                html: `
                <div style="width: 100%; padding: 2px 4px; font-size: 10px; line-height: 1.1; overflow: hidden; display: flex; flex-direction: column;">
                    ${durationHtml}
                    ${descriptionHtml}
                    ${projectHtml}
                    <div style="position: absolute; top: 2px; right: 2px;">${billableIconHtml}</div>
                </div>
                `
            };
        };

/**
 * The FullCalendar grid styles, scoped to `.gva-calendar` (the calendar's container Box).
 * They used to be part of that Box's `sx`, which MUI turns into CSS again on every render
 * of CalendarComponent -- a ~300-line object walked and hashed each time. As GlobalStyles
 * in a memoized component they are regenerated only when one of the six colours changes
 * (a theme switch). The rules are moved verbatim; duplicate keys such as
 * '& .fc-col-header-cell' behave as before (the last one wins). Keep them plain CSS
 * values: unlike `sx`, GlobalStyles does not resolve theme keys ('divider', spacing numbers).
 */
const CalendarGridStyles = React.memo(function CalendarGridStyles({
  calBg, calGrid, calGridSub, calGridFaint, calToday, calText,
}) {
  return (
    <GlobalStyles styles={{
      '.gva-calendar': {
        // FullCalendar root background
        '& .fc, & .fc-scrollgrid, & .fc-scrollgrid-section, & .fc-scrollgrid-section-body, & .fc-scrollgrid-section-header': {
          backgroundColor: `${calBg} !important`,
        },
        '& .fc-timegrid-body, & .fc-daygrid-body': {
          backgroundColor: `${calBg} !important`,
        },
        '& .fc-timegrid-slot-bg, & .fc-daygrid-day-bg': {
          backgroundColor: `${calBg} !important`,
        },
        
        '& .fc-event, & .fc-daygrid-event, & .fc-timegrid-event, & .fc-v-event': { 
          border: 'none !important',
          borderColor: 'transparent !important',
          borderWidth: '0 !important',
          boxShadow: 'none !important',
          outline: 'none !important',
          margin: '2px 0 !important',
          padding: '1px',
          borderRadius: '4px !important',
          overflow: 'hidden !important',
          display: 'flex !important',
          alignItems: 'center !important',
        },

     
        '& .fc-timegrid-slots tr': {
          height: '70px !important', // Reduced row height
        },

        '& .event-single-line': {
          whiteSpace: 'nowrap !important',
          overflow: 'hidden !important',
          textOverflow: 'ellipsis !important',
          fontSize: '0.75rem !important',
          width: '100% !important',
        },

     
        '& .fc-event-title, & .fc-event-time': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block', 
          maxWidth: '100%',
          paddingRight: '2px',
        },
        '& .fc-event-main-frame': {
          overflow: 'hidden',
        },

        // Day/Week Headers
        '& .fc-col-header-cell': {
            padding: '6px 4px',
            borderLeft: 'none !important', // Remove left border from header row
            borderRight: 'none !important', // Remove right border from header row
            fontSize: '0.75rem !important',
        },
        '& .fc-col-header-cell-cushion': {
            fontSize: '0.75rem !important',
        },
        // Round the top-left corner of the day header (for timeGridWeek/Day view)
        '& .fc-timegrid-axis': {
            borderTopLeftRadius: '10px',
        },
        // Round the top-right corner of the last day header
        '& .fc-col-header-cell:last-child': {
            borderTopRightRadius: '10px',
        },
        // Highlight today with subtle radius
        '& .fc-day-today': {
          borderRadius: '4px',
          backgroundColor: `${calToday} !important`,
          },
          '& .fc-timegrid-slot-today': {
          backgroundColor: `${calToday} !important`,
          },
          '& .fc-timegrid-col-today': {
          backgroundColor: `${calToday} !important`,
          },
          '& .fc-timegrid-col-today .fc-timegrid-slot': {
          backgroundColor: `${calToday} !important`,
          },
          '& .fc-timegrid-slot-label': {
              paddingRight: '12px',
              fontWeight: 'normal',
              fontSize: '0.7rem',
              color: calText,
          },
        // Calendar Grid Border Colors - Comprehensive (reduced opacity for less brightness)
        '& .fc-timegrid-slot, & .fc-timegrid-slot-frame': {
          borderBottomColor: `${calGrid} !important`,
          borderRightColor: `${calGrid} !important`,
          borderTopColor: `${calGrid} !important`,
          borderLeftColor: `${calGrid} !important`,
          borderWidth: '0.5px !important',
        },
        // Remove top and bottom borders from first column slots
        '& .fc-timegrid-col:first-of-type .fc-timegrid-slot, & .fc-timegrid-col:first-of-type .fc-timegrid-slot-frame': {
          borderTop: 'none !important',
          borderBottom: 'none !important',
        },
        '& .fc-timegrid-slot-minor': {
            borderBottomColor: `${calGrid} !important`,
            borderWidth: '0.5px !important',
        },
        // Remove top and bottom borders from first column minor slots
        '& .fc-timegrid-col:first-of-type .fc-timegrid-slot-minor': {
            borderTop: 'none !important',
            borderBottom: 'none !important',
        },
        '& .fc-daygrid-day, & .fc-daygrid-day-frame, & .fc-daygrid-day-top, & .fc-daygrid-day-bottom': {
            borderColor: `${calGrid} !important`,
            borderTopColor: `${calGrid} !important`,
            borderBottomColor: `${calGrid} !important`,
            borderLeftColor: `${calGrid} !important`,
            borderRightColor: `${calGrid} !important`,
            borderWidth: '0.5px !important',
        },
        '& .fc-col-header-cell': {
          padding: '8px 4px',
          borderLeft: 'none !important', // Remove left border from header row
          borderRight: 'none !important', // Remove right border from header row
          borderBottom: `0.5px solid ${calGridSub} !important`,
          borderTop: `0.5px solid ${calGridSub} !important`,
        },
        '& .fc-timegrid-divider': {
            borderColor: `${calGrid} !important`,
            borderWidth: '0.5px !important',
        },
        '& .fc-timegrid-axis': {
            borderColor: `${calGrid} !important`,
            borderTopColor: 'transparent !important', // Remove top border from time axis
            borderBottomColor: 'transparent !important', // Remove bottom border from time axis
            borderLeftColor: `${calGrid} !important`,
            borderRightColor: `${calGrid} !important`,
            borderWidth: '0.5px !important',
        },
        // Remove top and bottom borders from time axis cells (first column rows)
        '& .fc-timegrid-axis td, & .fc-timegrid-axis th': {
            borderTop: 'none !important',
            borderBottom: 'none !important',
        },
        '& .fc-timegrid-slot-label, & .fc-timegrid-slot-label-frame, & .fc-timegrid-slot-label-cushion': {
            borderTop: 'none !important',
            borderBottom: 'none !important',
        },

        '& .fc-timegrid-slot-bg, & .fc-daygrid-day-bg': {
          borderColor: `${calGrid} !important`,
          borderWidth: '0.5px !important',
      },
      // Vertical dividers and separators
      '& .fc-v-event, & .fc-timegrid-col, & .fc-daygrid-col': {
          borderRightColor: `${calGrid} !important`,
          borderLeftColor: `${calGrid} !important`,
          borderWidth: '0.5px !important',
      },
      '& .fc-timegrid-slot, & .fc-timegrid-slot-frame, & .fc-timegrid-slot-minor': {
          borderRightColor: `${calGrid} !important`,
          borderWidth: '0.5px !important',
      },
      // All table cells and grid elements
      '& td, & th': {
          borderRight: `0.5px solid ${calGrid} !important`,
          borderBottom: `0.5px solid ${calGrid} !important`,
      },
      // Header cells (date labels) - lower opacity for separators
      '& .fc-col-header-cell, & .fc-scrollgrid-section-header th': {
          borderTop: `0.5px solid ${calGridFaint} !important`,
          borderBottom: 'none !important',
      },
      // Scrollgrid borders
      '& .fc-scrollgrid-section > td': {
          borderRight: `0.5px solid ${calGrid} !important`,
      },
      
        // Additional grid elements
        '& .fc-scrollgrid, & .fc-scrollgrid-section, & .fc-scrollgrid-section-body': {
            borderColor: `${calGrid} !important`,
            borderWidth: '0.5px !important',
        },
        '& .fc-timegrid-slot-bg, & .fc-daygrid-day-bg': {
            borderColor: `${calGrid} !important`,
            borderWidth: '0.5px !important',
        },
        
        // Remove outer edge borders to make calendar span through edges
        '& .fc-scrollgrid': {
            borderTop: 'none !important',
            borderBottom: 'none !important',
            borderLeft: 'none !important',
            borderRight: 'none !important',
        },
        '& .fc-scrollgrid-section-header': {
            borderTop: 'none !important',
        },
        '& .fc-scrollgrid-section-footer': {
            borderBottom: 'none !important',
        },
        // Remove left border from first column
        '& .fc-timegrid-axis, & .fc-col-header-cell:first-of-type, & .fc-timegrid-col:first-of-type, & .fc-daygrid-col:first-of-type': {
            borderLeft: 'none !important',
        },
        // Remove right border from last column
        '& .fc-col-header-cell:last-of-type, & .fc-timegrid-col:last-of-type, & .fc-daygrid-col:last-of-type': {
            borderRight: 'none !important',
        },
        // Header row borders - keep top with lower opacity, remove bottom, left and right
        '& .fc-col-header-cell': {
            borderTop: `0.5px solid ${calGridFaint} !important`,
            borderBottom: 'none !important',
            borderLeft: 'none !important', // Ensure no left border on header row
            borderRight: 'none !important', // Ensure no right border on header row
        },
        // Remove left and right borders from header row table cells
        '& .fc-scrollgrid-section-header table th, & .fc-scrollgrid-section-header table td': {
            borderLeft: 'none !important',
            borderRight: 'none !important',
        },
        // Remove bottom border from last row
        '& .fc-timegrid-slot:last-of-type, & .fc-daygrid-day:last-of-type': {
            borderBottom: 'none !important',
        },
        '& .fc-scrollgrid-section-body table tbody tr:last-of-type td': {
            borderBottom: 'none !important',
        },
        // Remove top and bottom borders from all rows in the first column (time axis)
        '& .fc-scrollgrid-section-body table tbody tr td:first-of-type': {
            borderTop: 'none !important',
            borderBottom: 'none !important',
        },
        '& .fc-timegrid-body table tbody tr td:first-of-type': {
            borderTop: 'none !important',
            borderBottom: 'none !important',
        },
        '& .fc-timegrid-slot:first-of-type': {
            borderTop: 'none !important',
            borderBottom: 'none !important',
        },
        // Remove borders from time axis background cells
        '& .fc-timegrid-axis .fc-timegrid-slot-bg': {
            borderTop: 'none !important',
            borderBottom: 'none !important',
        },

        // --- EXISTING DAYGRID STYLES ---
        
        '& .fc-daygrid-day': {
          // Add more padding to
          padding: '10px 10px 10px 10px', 
        },
        '& .fc-daygrid-event': {
          margin: '1px 0 !important',
          borderRadius: '3px !important',
          minHeight: 'auto !important',
        },
        '& .fc-daygrid-day-events': {
          display: 'flex !important',
          flexDirection: 'column !important',
          gap: '1px !important',
        },
        '& .fc-daygrid-event-harness': {
          margin: '0 !important',
        },
        '& .fc-daygrid-day': {
          '& .fc-daygrid-day-events': {
            maxHeight: 'none !important',
          }
        },
        // Current time indicator line
        '& .fc-timegrid-now-indicator-line': {
          borderColor: '#39c6ed !important', // White color for visibility
          borderWidth: '2px 0 0 0 !important',
          opacity: '0.8 !important',
          zIndex: '1000 !important',
        },
        '& .fc-timegrid-now-indicator-arrow': {
          borderLeftColor: '#ffffff !important', // White color for arrow
          borderRightColor: '#ffffff !important',
          opacity: '0.8 !important',
        },

        /* Force events to take up full width even if they overlap slightly */
        '& .fc-timegrid-event': {
          minWidth: '0 !important',
          margin: '0 !important',
        },

        /* Ensure the container doesn't expand vertically */
        '& .fc-v-event .fc-event-main': {
          padding: '0 !important',
          overflow: 'hidden !important',
        },

        /* Fix the 'pushed to the side' look */
        '& .fc-timegrid-col-events':   {
          margin: '0 !important',
        }
      },
    }} />
  );
});

const CalendarComponent = ({ 
  timeEntries = [], 
  timeEntriesLoading = false,
  loadingAllMembers = false, // Loading state for fetching all members' entries
  clients = [], 
  projects = [], 
  members = [],
  isMembersLoading,
  teamspaces = [], // All teamspaces (not just those with entries)
  currentTimezone = 'UTC',
  timezoneReady = false, // Add timezoneReady prop to know when timezone is ready
  onOptimisticUpdate,
  onOptimisticCreate,
  onOptimisticDelete,
  groupMap,
  currentWeekStart,
  onEventUpdate, // Callback to refresh calendar after event update/create
  onDateRangeChange, // Callback when calendar date range changes
  onTimeEntryUpdated,
  onTimeEntryAdded,
  onTimeEntryDeleted,
  // Filter props - synced with URL
  selectedClients: propSelectedClients,
  setSelectedClients: propSetSelectedClients,
  selectedProjects: propSelectedProjects,
  setSelectedProjects: propSetSelectedProjects,
  selectedBillable: propSelectedBillable,
  setSelectedBillable: propSetSelectedBillable,
  selectedMember: propSelectedMember,
  setSelectedMember: propSetSelectedMember,
  selectedTeamspaces: propSelectedTeamspaces,
  setSelectedTeamspaces: propSetSelectedTeamspaces,
  projectsByTeamspace: propProjectsByTeamspace = {},
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Theme-aware calendar color tokens
  const calBg       = theme.palette.background.default;
  const calGrid     = isDark ? 'rgba(255,255,255,0.3)'  : 'rgba(0,0,0,0.10)';
  const calGridSub  = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)';
  const calGridFaint= isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)';
  const calToday    = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(222,49,99,0.05)';
  const calText     = theme.palette.text.secondary;
  const calTextMain = theme.palette.text.primary;

  // Format timezone for display (similar to Header)
  const formatTimezone = (tz) => {
    if (!tz || tz === 'UTC') return 'UTC';
    
    const timezoneMap = {
      'UTC': 'UTC',
      'Asia/Manila': 'Asia/Manila (UTC+8)',
      'Asia/Tokyo': 'Asia/Tokyo (UTC+9)',
      'Asia/Bangkok': 'Asia/Bangkok (UTC+7)',
      'Asia/Kolkata': 'Asia/Kolkata (UTC+5:30)',
    };
    
    if (timezoneMap[tz]) {
      return timezoneMap[tz];
    }
    
    // Extract city name from timezone string
    const parts = tz.split('/');
    if (parts.length > 1) {
      const city = parts[parts.length - 1].replace(/_/g, ' ');
      try {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en', {
          timeZone: tz,
          timeZoneName: 'shortOffset'
        });
        const parts = formatter.formatToParts(now);
        const offsetPart = parts.find(part => part.type === 'timeZoneName');
        if (offsetPart) {
          return `${city} (${offsetPart.value})`;
        }
      } catch (e) {
        // If formatting fails, just return the city name
      }
      return city;
    }
    
    return tz;
  };

  // Validate timezone and log if it's valid or if it failed
  const validatedTimezone = useMemo(() => {
    if (!currentTimezone || currentTimezone === 'UTC') {
      return 'UTC';
    }
    
    // Check if timezone is valid using Intl API
    try {
      // Try to create a date formatter with the timezone to validate it
      const testDate = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: currentTimezone,
        timeZoneName: 'short'
      });
      formatter.format(testDate);
      
      // If we get here, the timezone is valid
      console.log('✅ [CalendarComponent] Timezone validation successful:', currentTimezone);
      return currentTimezone;
    } catch (error) {
      console.error('❌ [CalendarComponent] Timezone validation failed:', {
        attemptedTimezone: currentTimezone,
        error: error.message,
        fallbackToUTC: true,
        note: 'FullCalendar will use UTC as fallback'
      });
      
      // Log correct timezone format information
      try {
        // Get list of supported timezones (if available)
        if (Intl.supportedValuesOf) {
          const supportedTimezones = Intl.supportedValuesOf('timeZone');
          const similarTimezones = supportedTimezones.filter(tz => 
            tz.toLowerCase().includes(currentTimezone.toLowerCase().split('/')[0]?.toLowerCase() || '')
          ).slice(0, 10); // Show first 10 similar timezones
          
          console.log('📋 [CalendarComponent] Correct timezone format (IANA timezone names):', {
            attempted: currentTimezone,
            format: 'Use IANA timezone names like "Asia/Manila", "America/New_York", "Europe/London"',
            examples: similarTimezones.length > 0 ? similarTimezones : [
              'Asia/Manila',
              'Asia/Bangkok',
              'Asia/Tokyo',
              'America/New_York',
              'America/Los_Angeles',
              'Europe/London',
              'Europe/Paris',
              'Australia/Sydney'
            ],
            fullList: 'See: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones'
          });
        } else {
          console.log('📋 [CalendarComponent] Correct timezone format (IANA timezone names):', {
            attempted: currentTimezone,
            format: 'Use IANA timezone names like "Asia/Manila", "America/New_York", "Europe/London"',
            examples: [
              'Asia/Manila',
              'Asia/Bangkok',
              'Asia/Tokyo',
              'America/New_York',
              'America/Los_Angeles',
              'Europe/London',
              'Europe/Paris',
              'Australia/Sydney'
            ],
            fullList: 'See: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones'
          });
        }
      } catch (e) {
        console.log('📋 [CalendarComponent] Correct timezone format (IANA timezone names):', {
          attempted: currentTimezone,
          format: 'Use IANA timezone names like "Asia/Manila", "America/New_York", "Europe/London"',
          examples: [
            'Asia/Manila',
            'Asia/Bangkok',
            'Asia/Tokyo',
            'America/New_York',
            'America/Los_Angeles',
            'Europe/London',
            'Europe/Paris',
            'Australia/Sydney'
          ],
          fullList: 'See: https://en.wikipedia.org/wiki/List_of_tz_database_time_zones'
        });
      }
      
      return 'UTC'; // Fallback to UTC
    }
  }, [currentTimezone]);
  
  // Log the currentTimezone value being used
  useEffect(() => {
    console.log('📅 [CalendarComponent] currentTimezone prop received:', currentTimezone);
    console.log('📅 [CalendarComponent] validatedTimezone:', validatedTimezone, {
      isValid: validatedTimezone === currentTimezone,
      isFallback: validatedTimezone === 'UTC' && currentTimezone !== 'UTC'
    });
  }, [currentTimezone, validatedTimezone]);
  
  const calendarRef = useRef(null);
  const isPhone = useIsPhone();
  // Phones open on a single day; desktop and tablets on the week, as before.
  const [currentView, setCurrentView] = useState(() => (isPhone ? 'timeGridDay' : 'timeGridWeek'));
  const [phoneFiltersOpen, setPhoneFiltersOpen] = useState(false);
  // Phones: start on today when it falls in the page's week, else on that Monday.
  // FullCalendar reads this only at mount. "Today" is the teamspace's date, pinned to
  // noon like currentWeekStart: FullCalendar (named timeZone, no tz plugin) reads a
  // Date by its UTC parts, so a bare `new Date()` opened Asia phones on yesterday
  // until UTC midnight.
  const phoneInitialDate = useMemo(() => {
    let today;
    try {
      today = validatedTimezone ? utcToZonedTime(new Date(), validatedTimezone) : new Date();
    } catch (e) {
      today = new Date();
    }
    today.setHours(12, 0, 0, 0);
    if (!currentWeekStart) return today;
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    return today >= currentWeekStart && today < weekEnd ? today : currentWeekStart;
  }, [currentWeekStart, validatedTimezone]);
  const [currentDateRange, setCurrentDateRange] = useState({ start: null, end: null });

  const { selectedTeamspaceId, teamspaceIdToNameMap } = useTeamspace();
  const { user, role_dict } = useAuth();

  // Use filter props if provided (from CalendarPage with URL sync), otherwise use local state
  const [localSelectedClients, setLocalSelectedClients] = useState([]);
  const [localSelectedProjects, setLocalSelectedProjects] = useState([]);
  const [localSelectedBillable, setLocalSelectedBillable] = useState('all');
  const [localSelectedMember, setLocalSelectedMember] = useState('');
  const [localSelectedTeamspaces, setLocalSelectedTeamspaces] = useState([]);
  
  // Use props if provided, otherwise fall back to local state
  const selectedClients = propSelectedClients !== undefined ? propSelectedClients : localSelectedClients;
  const setSelectedClients = propSetSelectedClients || setLocalSelectedClients;
  const selectedProjects = propSelectedProjects !== undefined ? propSelectedProjects : localSelectedProjects;
  const setSelectedProjects = propSetSelectedProjects || setLocalSelectedProjects;
  const selectedBillable = propSelectedBillable !== undefined ? propSelectedBillable : localSelectedBillable;
  const setSelectedBillable = propSetSelectedBillable || setLocalSelectedBillable;
  const selectedMember = propSelectedMember !== undefined ? propSelectedMember : localSelectedMember;
  const setSelectedMember = propSetSelectedMember || setLocalSelectedMember;
  const selectedTeamspaces = propSelectedTeamspaces !== undefined ? propSelectedTeamspaces : localSelectedTeamspaces;
  const setSelectedTeamspaces = propSetSelectedTeamspaces || setLocalSelectedTeamspaces;
  // Quick form states
  const [quickFormAnchor, setQuickFormAnchor] = useState(null);
  const [quickFormData, setQuickFormData] = useState(null); // null for new, entry object for edit
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  // Edit modal state (for opening EditTimeEntryForm when clicking existing calendar entries)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalEntry, setEditModalEntry] = useState(null);

  // Group drag confirmation dialog
  // Stores pending drop info until user chooses apply-single / apply-all / cancel
  const [groupDropDialog, setGroupDropDialog] = useState(null);
  // { entry, groupEntries, startTimeUTC, endTimeUTC, revert }

  // Details dialog state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEntryForDetails, setSelectedEntryForDetails] = useState(null);

  // ── Live presence on events ────────────────────────────────────────────────
  // An entry counts as being edited while its full edit form or its Quick form
  // (opened from the details view's Edit) is open. Grid key 'time_entries' is
  // shared with the Time Entries page, so the "also editing this" notice works
  // across the two pages; restricted DCC Staff never receive it (server side).
  // Markers are pure CSS keyed on data-entry-id (CalendarPresenceMarkers), so
  // FullCalendar's events array -- which must stay stable -- never changes.
  const { setEditing: setEntryPresenceEditing } = useGridPresence('time_entries');
  const editingEntryId = editModalOpen && editModalEntry?.id != null
    ? editModalEntry.id
    : (quickFormAnchor && quickFormData?.id != null ? quickFormData.id : null);
  useEffect(() => { setEntryPresenceEditing(editingEntryId); }, [editingEntryId, setEntryPresenceEditing]);
  const presenceLabelsRef = useRef({});
  // Stable (no deps): FullCalendar gets the same function every render.
  const handleEventDidMount = useCallback((info) => {
    const id = info.event.id;
    info.el.setAttribute('data-entry-id', id);
    const title = presenceLabelsRef.current[id];
    if (title) {
      info.el.setAttribute('title', title);
      info.el.setAttribute('data-presence-title', '1');
    }
  }, []);

  // Project lookup state - per-teamspace map of project ID to project object.
  // Seeded from the pre-loaded propProjectsByTeamspace (from CalendarPage's optionsByTeamspace
  // cache) so project names resolve immediately without extra API calls.
  const projectLookupByTeamspace = useMemo(() => {
    if (!propProjectsByTeamspace || Object.keys(propProjectsByTeamspace).length === 0) return {};
    return propProjectsByTeamspace;
  }, [propProjectsByTeamspace]);

  // Category lookup state - per-teamspace map of category ID to category name
  // (Needed because the calendar can show entries from multiple teamspaces.)
  const [categoryLookupByTeamspace, setCategoryLookupByTeamspace] = useState({});
  const fetchedCategoryTeamspacesRef = useRef(new Set());

  // Backward-compatible lookup for filters/chips (uses the currently selected teamspace)
  const categoryLookup = useMemo(() => {
    if (!selectedTeamspaceId) return {};
    return categoryLookupByTeamspace[String(selectedTeamspaceId)] || {};
  }, [categoryLookupByTeamspace, selectedTeamspaceId]);

  // Fetch categories for any teamspaces currently present in the calendar entries
  useEffect(() => {
    if (!timeEntries || timeEntries.length === 0) return;

    const uniqueTeamspaceIds = [...new Set(
      timeEntries
        .map(e => e.teamspace_id)
        .filter(id => id != null && id !== '' && !Number.isNaN(Number(id)))
        .map(id => String(Number(id)))
    )];

    const teamspaceIdsToFetch = uniqueTeamspaceIds.filter(id => !fetchedCategoryTeamspacesRef.current.has(id));
    if (teamspaceIdsToFetch.length === 0) return;

    let cancelled = false;

    const fetchCategoriesForTeamspace = async (teamspaceIdStr) => {
      const categoriesUrl = `${API_BASE_URL}teamspaces/${teamspaceIdStr}/time_entry_categories/`;
      let allCategories = [];
      let currentUrl = categoriesUrl;
      try {
        while (currentUrl) {
          const categoriesResponse = await axios.get(currentUrl);
          const data = categoriesResponse.data;
          if (data.results) {
            allCategories = [...allCategories, ...data.results];
            currentUrl = data.next;
          } else if (Array.isArray(data)) {
            allCategories = [...allCategories, ...data];
            currentUrl = null;
          } else {
            break;
          }
        }
      } catch (error) {
        // If endpoint doesn't exist or fails, just return empty lookup for this teamspace
        console.warn(`Time entry categories endpoint not available for teamspace ${teamspaceIdStr}:`, error);
        return {};
      }

      return allCategories.reduce((map, item) => {
        map[String(item.id)] = item.name || '';
        return map;
      }, {});
    };

    const run = async () => {
      const results = await Promise.all(
        teamspaceIdsToFetch.map(async (tsId) => [tsId, await fetchCategoriesForTeamspace(tsId)])
      );

      if (cancelled) return;

      setCategoryLookupByTeamspace(prev => {
        const next = { ...prev };
        results.forEach(([tsId, lookup]) => {
          next[String(tsId)] = lookup || {};
        });
        return next;
      });

      teamspaceIdsToFetch.forEach(tsId => fetchedCategoryTeamspacesRef.current.add(String(tsId)));
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [timeEntries]);

  // Determine user permissions
  const teamspaceName = useMemo(() => {
    return teamspaceIdToNameMap[selectedTeamspaceId] || '';
  }, [selectedTeamspaceId, teamspaceIdToNameMap]);
  
  const isStaff = user && teamspaceName && role_dict[teamspaceName]?.role?.includes('Staff');
  const canCreateForOthers = !!(user && teamspaceName && role_dict[teamspaceName]?.role?.some(r => ['Admin', 'Developer', 'Lead'].includes(r)));

  // Check if user can edit a specific entry
  const canEditEntry = useCallback((entry) => {
    if (canCreateForOthers) return true; // Admin, Developer, Lead can edit any entry
    if (!isStaff) return true; // Other non-staff roles can also edit any entry

    // Staff can only edit their own entries
    return entry.member_username === user?.username || entry.member_username === user?.email;
  }, [canCreateForOthers, isStaff, user?.username, user?.email]);

  // Stable identity, so FullCalendar doesn't see a changed option on every render.
  const handleDatesSet = useCallback((dateInfo) => {
    if (onDateRangeChange && dateInfo.start && dateInfo.end) {
      // "Safe Date": the Wednesday in week view; the middle of the range in the
      // phone's Day / 3-day views (see utils/calendarRange).
      onDateRangeChange(weekAnchorDate(dateInfo.start, dateInfo.end), dateInfo.end);
    }
  }, [onDateRangeChange]);

  const handleViewChange = (view) => {
    setCurrentView(view);
    // Add a small delay to ensure state has updated
    setTimeout(() => {
      if (calendarRef.current) {
        try {
          const calendarApi = calendarRef.current.getApi();
          if (calendarApi) {
            calendarApi.changeView(view);
            // Update date range after view change
            updateDateRange(calendarApi);
          }
        } catch (error) {
          console.error('Error changing calendar view:', error);
        }
      }
    }, 0);
  };

  const updateDateRange = (calendarApi) => {
    if (calendarApi) {
      const view = calendarApi.view;
      setCurrentDateRange({
        start: view.currentStart,
        end: view.currentEnd
      });
    }
  };

  const handleToday = () => {
    if (calendarRef.current) {
      try {
        const calendarApi = calendarRef.current.getApi();
        if (calendarApi) {
          calendarApi.today();
          updateDateRange(calendarApi);
        }
      } catch (error) {
        console.error('Error navigating to today:', error);
      }
    }
  };

  const handlePrev = () => {
    if (calendarRef.current) {
      try {
        const calendarApi = calendarRef.current.getApi();
        if (calendarApi) {
          calendarApi.prev();
          updateDateRange(calendarApi);
        }
      } catch (error) {
        console.error('Error navigating to previous:', error);
      }
    }
  };

  const handleNext = () => {
    if (calendarRef.current) {
      try {
        const calendarApi = calendarRef.current.getApi();
        if (calendarApi) {
          calendarApi.next();
          updateDateRange(calendarApi);
        }
      } catch (error) {
        console.error('Error navigating to next:', error);
      }
    }
  };
  // Set default member filter to current user when component mounts or user changes
  // Only if filters are managed locally (not via props)
  useEffect(() => {
    if (propSelectedMember === undefined && user) {
      const currentUserIdentifier = user?.username || user?.email || '';
      // Only set if we have a user identifier and selectedMember is empty or not set
      if (currentUserIdentifier && (!localSelectedMember || localSelectedMember === '')) {
        setLocalSelectedMember(currentUserIdentifier);
      }
    }
  }, [user, propSelectedMember, localSelectedMember]); // Only depend on user, not selectedMember to avoid loops

  const handleClearFilters = () => {
    const currentUserIdentifier = user?.username || user?.email || '';
    setSelectedClients([]);
    setSelectedProjects([]);
    setSelectedBillable('all');
    setSelectedMember(currentUserIdentifier); // Reset to current user
    setSelectedTeamspaces([]);
  };

  // Lookups built once per list change instead of once per entry or per render.
  // Maps reproduce `list.find(x => x.id === id)` exactly: strict key matching, first match wins.
  const projectMap = useMemo(() => {
    const map = new Map();
    for (const p of projects) if (!map.has(p.id)) map.set(p.id, p);
    return map;
  }, [projects]);

  const clientMap = useMemo(() => {
    const map = new Map();
    for (const c of clients) if (!map.has(c.id)) map.set(c.id, c);
    return map;
  }, [clients]);

  // The details dialog indexes these as plain objects — built exactly as the inline
  // `reduce` did (a later duplicate id overwrites an earlier one), but once per list change.
  const projectLookupForDetails = useMemo(() => {
    const lookup = {};
    for (const p of projects) lookup[p.id] = p;
    return lookup;
  }, [projects]);

  const clientLookupForDetails = useMemo(() => {
    const lookup = {};
    for (const c of clients) lookup[c.id] = c;
    return lookup;
  }, [clients]);

  const filteredTimeEntries = useMemo(() => {
    // Resolved once per pass rather than once per entry — it doesn't depend on the entry.
    // Find the member object that matches the selectedMember (by label, username, or email)
    const matchingMember = selectedMember
      ? members.find(m =>
          m.label === selectedMember ||
          m.member_username === selectedMember ||
          m.email === selectedMember
        )
      : undefined;
    // If we found a matching member, use their username; fall back to selectedMember if username is null/empty.
    // If no matching member found, compare directly with selectedMember.
    const expectedUsername = matchingMember
      ? (matchingMember.username || selectedMember)
      : selectedMember;

    const filtered = timeEntries.filter(entry => {
      // Member filter - only show entries for the selected member
      if (selectedMember) {
        if (entry.member_username !== expectedUsername && entry.member_username !== selectedMember) {
          return false;
        }
      }

      if (selectedTeamspaces.length > 0 && !selectedTeamspaces.includes(entry.teamspace_id)) {
        return false;
      }
      
      // Client filter - get client from project
      if (selectedClients.length > 0) {
        const project = projectMap.get(entry.project);
        const client = project ? clientMap.get(project.client) : null;
        if (!client || !selectedClients.includes(client.code)) {
          return false;
        }
      }
      
      // Project filter (includes categories)
      if (selectedProjects.length > 0) {
        // Check if entry matches a selected project
        const matchesProject = entry.project && selectedProjects.includes(entry.project);
        // Check if entry matches a selected category (categories are stored as "cat:{id}" in selectedProjects)
        const matchesCategory = entry.category && selectedProjects.includes(`cat:${entry.category}`);
        if (!matchesProject && !matchesCategory) {
          return false;
        }
      }
      
      // Billable filter
      if (selectedBillable !== 'all') {
        const isBillable = entry.billable !== false;
        if (selectedBillable === 'billable' && !isBillable) return false;
        if (selectedBillable === 'non-billable' && isBillable) return false;
      }
      
      return true;
    });
    
    return filtered;
  }, [timeEntries, selectedClients, selectedProjects, selectedBillable, selectedMember, selectedTeamspaces, projectMap, clientMap, members]);
  
  const teamspaceColorMap = useMemo(() => {
    const teamspaceColors = [
      '#69162f',
      '#1d2975',
      '#1a571a',
      '#FF6347',
      '#8a6128',
      '#FF1493',
      '#00CED1',
      '#9370DB'
    ]

    // Use all teamspaces (not just those with entries) to ensure consistent color mapping
    // This prevents colors from changing when entries are filtered
    const allTeamspaceIds = teamspaces
      .map(ts => ts.id)
      .filter(id => id != null)
      .sort((a, b) => a - b);

    const colorMap = {};
    allTeamspaceIds.forEach((teamspaceId, index) => {
      const colorIndex = index % teamspaceColors.length;
      colorMap[teamspaceId] = teamspaceColors[colorIndex];
    });

    console.log('🎨 [CalendarComponent] Teamspace color map (fixed per teamspace):', colorMap);
    return colorMap;
    }, [teamspaces]);
  
  // Create legend data combining teamspace names with colors
const teamspaceLegend = useMemo(() => {
  // Normalize teamspace_id to numbers for proper deduplication
  const normalizedTeamspaceIds = timeEntries
    .map(entry => entry.teamspace_id)
    .filter(id => id != null && id !== undefined && id !== '')
    .map(id => Number(id))
    .filter(id => !isNaN(id));
  
  const uniqueTeamspaces = [...new Set(normalizedTeamspaceIds)];
  
return uniqueTeamspaces
  .map(teamspaceId => ({
    id: teamspaceId,
    name: teamspaceIdToNameMap[teamspaceId] || `Teamspace ${teamspaceId}`,
    color: teamspaceColorMap[teamspaceId] || '#9E9E9E'
  }))
  .sort((a, b) => a.name.localeCompare(b.name)); 
}, [timeEntries, teamspaceColorMap, teamspaceIdToNameMap]);
  
    

  
  
  // Parse dates separately - only depends on timeEntries, not on projects/clients/members
  // This prevents date recalculation when options load
  const parsedTimeEntries = useMemo(() => {
    if (!timezoneReady || !validatedTimezone) {
      return [];
    }
    
    if (!filteredTimeEntries || filteredTimeEntries.length === 0) {
      return [];
    }
    
    const parsed = filteredTimeEntries.map(entry => {
      if (!entry.start_time) {
        console.warn('⚠️ [CalendarComponent] Entry missing start_time:', {
          entryId: entry.id,
          entry: entry
        });
        return { entry, startDate: null, endDate: null, valid: false };
      }
      
      // Parse dates directly (already converted by CalendarPage.js)
      const startDate = entry.start_time ? new Date(entry.start_time) : null;
      const endDate = entry.end_time ? new Date(entry.end_time) : null;
      
      // Validate dates
      const valid = startDate && !isNaN(startDate.getTime()) && (!endDate || !isNaN(endDate.getTime()));
      
      if (!valid) {
        console.warn('⚠️ [CalendarComponent] Entry has invalid dates:', {
          entryId: entry.id,
          start_time: entry.start_time,
          end_time: entry.end_time,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          startValid: startDate && !isNaN(startDate.getTime()),
          endValid: !endDate || !isNaN(endDate.getTime())
        });
      }
      
      return { entry, startDate, endDate, valid };
    });
    
    const validEntries = parsed.filter(item => item.valid);
    
    return validEntries;
  }, [filteredTimeEntries, timezoneReady, validatedTimezone]);
  
  // Create calendar events - depends on projects/clients for metadata, but uses pre-parsed dates
  const calendarEvents = useMemo(() => {
    // Don't create events until timezone is ready and validated
    if (!timezoneReady || !validatedTimezone || parsedTimeEntries.length === 0) {
      return [];
    }
    
    const events = parsedTimeEntries.map(({ entry, startDate, endDate }) => {
      const entryTeamspaceIdStr = entry.teamspace_id != null ? String(Number(entry.teamspace_id)) : '';
      const projectFromTeamspace =
        entry.project != null && entry.project !== '' && entryTeamspaceIdStr
          ? projectLookupByTeamspace?.[entryTeamspaceIdStr]?.[String(entry.project)]
          : null;
      const project = projectFromTeamspace || projectMap.get(entry.project);
      const client = project ? clientMap.get(project.client) : null;
      
      const projectName = project ? (project.full_project_name || project.name) : '';
      const categoryName = entry.category != null && entry.category !== ''
        ? (categoryLookupByTeamspace?.[entryTeamspaceIdStr]?.[String(entry.category)] || '')
        : '';
      const projectOrCategoryName = projectName || categoryName;
      const clientName = client ? client.name : 'Unknown Client';
      
      // Create title with description first, then project
      const title = entry.description 
        ? (projectOrCategoryName ? `${entry.description} [${projectOrCategoryName}]` : entry.description)
        : (projectOrCategoryName || '');
      
      // Color coding based on billable status
      const isBillable = entry.billable !== false;
      const canEdit = canEditEntry(entry);
      
      // Use teamspace color for background, with fallback
      const backgroundColor = entry.teamspace_id && teamspaceColorMap[entry.teamspace_id] 
        ? teamspaceColorMap[entry.teamspace_id] 
        : '#9E9E9E';
      const borderColor = 'transparent';
      
      // Log if teamspace_id is missing or color not found
      if (!entry.teamspace_id) {
        console.warn('⚠️ [CalendarComponent] Entry missing teamspace_id:', entry);
      } else if (!teamspaceColorMap[entry.teamspace_id]) {
        console.warn('⚠️ [CalendarComponent] No color found for teamspace_id:', entry.teamspace_id, 'Available colors:', Object.keys(teamspaceColorMap));
      }
      
      // Use pre-parsed dates - no recalculation here
      return {
        id: entry.id.toString(),
        title,
        start: startDate, // Pre-parsed date (already converted by CalendarPage.js)
        end: endDate,     // Pre-parsed date (already converted by CalendarPage.js)
        backgroundColor,
        borderColor,
        textColor: '#ffffff',
        // Only allow editing if user has permission. Phones: no drag/resize (it fights
        // scrolling) — times are edited through the form instead.
        editable: canEdit && !isPhone,
        extendedProps: {
          entry,
          project: projectOrCategoryName,
          client: clientName,
          duration: entry.duration,
          rate: entry.rate,
          amount: entry.amount,
          currency: entry.currency,
          billable: isBillable,
          canEdit: canEdit
        }
      };
    });
    
    return events;
  }, [parsedTimeEntries, projectMap, clientMap, canEditEntry, teamspaceColorMap, categoryLookupByTeamspace, projectLookupByTeamspace, timezoneReady, validatedTimezone, isPhone]);

  // Track previous timezone to detect changes
  const prevTimezoneRef = useRef(null);
  
  // Log when timezone changes (the key prop will handle the reload)
  useEffect(() => {
    if (timezoneReady && validatedTimezone) {
      const timezoneChanged = prevTimezoneRef.current !== validatedTimezone;
      if (timezoneChanged) {
        console.log('🔄 [CalendarComponent] Timezone changed - calendar will reload:', {
          old: prevTimezoneRef.current,
          new: validatedTimezone,
          timezoneReady: timezoneReady
        });
        prevTimezoneRef.current = validatedTimezone;
      }
    }
  }, [validatedTimezone, timezoneReady]);
  
  // Note: timezone is set via the timeZone prop in FullCalendar JSX
  // No need to set it again after mounting to avoid double conversion

  // NOTE: We do NOT manually call calendarApi.render() or refetchEvents() when
  // timeEntries/calendarEvents change. FullCalendar receives events via the `events` prop;
  // when React re-renders with new props, FullCalendar updates automatically.
  // Calling render()/refetchEvents() here caused double-updates and visible glitching.

  const handleTimeSlotSelect = (selectInfo) => {
    // Check permissions before allowing creation of new time entries
    // Admin, Developer, Lead can create for any member; Staff can only create for themselves
    if (!canCreateForOthers && isStaff && selectedMember !== user?.username && selectedMember !== user?.email) {
      return;
    }
    
    // Open quick form for creating new time entry
    setSelectedTimeSlot({ start: selectInfo.start, end: selectInfo.end });
    setQuickFormData(null); // null means creating new entry
    
    
    let anchorTarget;
    if (selectInfo.jsEvent) {
      // Try to use the actual clicked element (like edit does)
      // The target might be the calendar cell or a child element
      const clickedElement = selectInfo.jsEvent.target;
      
      // If the clicked element is part of the calendar, use it
      // Otherwise, try to find the parent cell element
      if (clickedElement && clickedElement.closest) {
        const cellElement = clickedElement.closest('.fc-daygrid-day, .fc-timegrid-slot, .fc-timegrid-col');
        anchorTarget = cellElement || clickedElement;
      } else {
        // Fallback to coordinates if element not found
        anchorTarget = { 
          clientX: selectInfo.jsEvent.clientX, 
          clientY: selectInfo.jsEvent.clientY 
        };
      }
    } else if (currentView === 'dayGridMonth') {
      // Fallback for month view selection if no click event is available 
      anchorTarget = calendarRef.current?.el || null; 
    } else {
      anchorTarget = null;
    }
    
    setQuickFormAnchor(anchorTarget);
  };

  // Phones: a tap on an empty slot opens the quick form for that hour (selecting
  // needs a press-and-hold on touch screens). Not passed to FullCalendar on desktop.
  const handlePhoneDateClick = (info) => {
    handleTimeSlotSelect({
      start: info.date,
      end: new Date(info.date.getTime() + 60 * 60 * 1000),
      jsEvent: info.jsEvent,
    });
  };

  // Crossing the phone breakpoint (rotation, window resize) switches between the
  // phone's Day view and the desktop week. Never runs on a desktop that stays wide.
  const prevIsPhoneRef = useRef(isPhone);
  useEffect(() => {
    if (prevIsPhoneRef.current === isPhone) return;
    prevIsPhoneRef.current = isPhone;
    handleViewChange(isPhone ? 'timeGridDay' : 'timeGridWeek');
  }, [isPhone]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleEventClick = (info) => {
      const entry = info.event.extendedProps.entry;
      const canEdit = canEditEntry(entry);
      
      if (canEdit) {
      // Use original UTC times if available (from CalendarPage conversion)
      // Otherwise use the entry times as-is (they're already UTC from API)
      const entryForForm = {
        ...entry,
        // Normalize teamspace for forms: some entries come through as `teamspace_id`
        // but forms expect `teamspace` (and use it to load the correct lookups).
        teamspace: entry.teamspace ?? entry.teamspace_id ?? selectedTeamspaceId,
        start_time: entry._original_start_time_utc || entry.start_time,
        end_time: entry._original_end_time_utc || entry.end_time,
      };
      
      setEditModalEntry(entryForForm);
      setEditModalOpen(true);
      } else {
        setSelectedEntryForDetails(entry);
        setDetailsOpen(true);
      }
      
      info.jsEvent.preventDefault();
    };

  const handleEventDrop = async (eventDropInfo) => {
    // Handle dragging existing events to reschedule
    const event = eventDropInfo.event;
    const entry = event.extendedProps.entry;
    
    // Check permissions
    if (!canEditEntry(entry)) {
      eventDropInfo.revert();
      return;
    }
    
    // FullCalendar with timeZone prop: event.start and event.end are Date objects
    // where the UTC components represent the LOCAL time in the display timezone.
    // Convert from display timezone to UTC for API submission.
    
    // Use validatedTimezone (from header) for conversion
    const displayTimezone = validatedTimezone || currentTimezone || 'UTC';
    
    let startTimeUTC;
    let endTimeUTC = null;
    
    if (displayTimezone && displayTimezone !== 'UTC') {
      // Extract local time components from FullCalendar's Date object
      // These UTC components represent the local time in the display timezone
      const startDate = event.start;
      const localYear = startDate.getUTCFullYear();
      const localMonth = startDate.getUTCMonth() + 1; // dayjs uses 1-based months
      const localDay = startDate.getUTCDate();
      const localHours = startDate.getUTCHours();
      const localMinutes = startDate.getUTCMinutes();
      const localSeconds = startDate.getUTCSeconds();
      
      // Create date string from local time components
      const startDateStr = `${localYear}-${String(localMonth).padStart(2, '0')}-${String(localDay).padStart(2, '0')}T${String(localHours).padStart(2, '0')}:${String(localMinutes).padStart(2, '0')}:${String(localSeconds).padStart(2, '0')}`;
      
      // Use dayjs to interpret this as a time in displayTimezone, then convert to UTC
      const startDayjs = dayjs.tz(startDateStr, displayTimezone);
      startTimeUTC = startDayjs.utc().toISOString();
      
      if (event.end) {
        const endDate = event.end;
        const endLocalYear = endDate.getUTCFullYear();
        const endLocalMonth = endDate.getUTCMonth() + 1;
        const endLocalDay = endDate.getUTCDate();
        const endLocalHours = endDate.getUTCHours();
        const endLocalMinutes = endDate.getUTCMinutes();
        const endLocalSeconds = endDate.getUTCSeconds();
        
        const endDateStr = `${endLocalYear}-${String(endLocalMonth).padStart(2, '0')}-${String(endLocalDay).padStart(2, '0')}T${String(endLocalHours).padStart(2, '0')}:${String(endLocalMinutes).padStart(2, '0')}:${String(endLocalSeconds).padStart(2, '0')}`;
        
        const endDayjs = dayjs.tz(endDateStr, displayTimezone);
        endTimeUTC = endDayjs.utc().toISOString();
      }
    } else {
      // UTC timezone - no conversion needed, but ensure it's in UTC format
      startTimeUTC = event.start.toISOString();
      endTimeUTC = event.end ? event.end.toISOString() : null;
    }
    
    // Validate times are in UTC format (ISO string ending with 'Z')
    if (!startTimeUTC || !startTimeUTC.endsWith('Z')) {
      console.error('❌ [CalendarComponent] Start time not in UTC format:', startTimeUTC);
      eventDropInfo.revert();
      return;
    }
    if (endTimeUTC && !endTimeUTC.endsWith('Z')) {
      console.error('❌ [CalendarComponent] End time not in UTC format:', endTimeUTC);
      eventDropInfo.revert();
      return;
    }
    
    console.log('🔄 [CalendarComponent] Event dropped - converting to UTC:', {
      entryId: entry.id,
      displayTimezone: displayTimezone,
      eventStart: event.start.toISOString(),
      eventEnd: event.end?.toISOString(),
      startTimeUTC,
      endTimeUTC,
      conversion: displayTimezone !== 'UTC' ? {
        from: displayTimezone,
        to: 'UTC',
        localTime: {
          year: event.start.getUTCFullYear(),
          month: event.start.getUTCMonth() + 1,
          day: event.start.getUTCDate(),
          hours: event.start.getUTCHours(),
          minutes: event.start.getUTCMinutes(),
          note: 'These represent local time in display timezone'
        },
        method: 'dayjs.tz().utc()'
      } : 'No conversion needed (UTC)',
      note: 'Converted from header timezone to UTC for API submission'
    });
    
    // Don't refresh the calendar UI - let FullCalendar keep the event where the user dropped it
    // Only update the backend data silently via optimistic update
    
    // If this is a grouped entry, show a confirmation dialog before applying
    const droppedEntryGroupEntries = groupMap ? groupMap.get(String(entry.id)) : null;
    const isGroupEntry = droppedEntryGroupEntries && droppedEntryGroupEntries.length > 1;

    if (isGroupEntry) {
      // Suspend the drop decision — show dialog to let user choose scope
      setGroupDropDialog({
        type: 'drop',
        entry,
        groupEntries: droppedEntryGroupEntries,
        startTimeUTC,
        endTimeUTC,
        revert: eventDropInfo.revert.bind(eventDropInfo),
      });
      return;
    }

    // Non-group entry — apply immediately
    try {
      if (onOptimisticUpdate) {
        await onOptimisticUpdate(entry.id, {
          start_time: startTimeUTC,
          end_time: endTimeUTC,
          teamspace: entry.teamspace_id || selectedTeamspaceId,
        });
      }
    } catch (error) {
      console.error('Error updating time entry:', error);
      eventDropInfo.revert();
    }
  };

  const handleEventResize = async (eventResizeInfo) => {
    // Handle resizing events (changing duration)
    const event = eventResizeInfo.event;
    const entry = event.extendedProps.entry;
    
    // Check permissions
    if (!canEditEntry(entry)) {
      eventResizeInfo.revert();
      return;
    }
    
    // FullCalendar with timeZone prop: event.start and event.end are Date objects
    // where the UTC components represent the LOCAL time in the display timezone.
    // Convert from display timezone to UTC for API submission.
    
    // Use validatedTimezone (from header) for conversion
    const displayTimezone = validatedTimezone || currentTimezone || 'UTC';
    
    let startTimeUTC;
    let endTimeUTC = null;
    
    if (displayTimezone && displayTimezone !== 'UTC') {
      // Extract local time components from FullCalendar's Date object
      // These UTC components represent the local time in the display timezone
      const startDate = event.start;
      const localYear = startDate.getUTCFullYear();
      const localMonth = startDate.getUTCMonth() + 1; // dayjs uses 1-based months
      const localDay = startDate.getUTCDate();
      const localHours = startDate.getUTCHours();
      const localMinutes = startDate.getUTCMinutes();
      const localSeconds = startDate.getUTCSeconds();
      
      // Create date string from local time components
      const startDateStr = `${localYear}-${String(localMonth).padStart(2, '0')}-${String(localDay).padStart(2, '0')}T${String(localHours).padStart(2, '0')}:${String(localMinutes).padStart(2, '0')}:${String(localSeconds).padStart(2, '0')}`;
      
      // Use dayjs to interpret this as a time in displayTimezone, then convert to UTC
      const startDayjs = dayjs.tz(startDateStr, displayTimezone);
      startTimeUTC = startDayjs.utc().toISOString();
      
      if (event.end) {
        const endDate = event.end;
        const endLocalYear = endDate.getUTCFullYear();
        const endLocalMonth = endDate.getUTCMonth() + 1;
        const endLocalDay = endDate.getUTCDate();
        const endLocalHours = endDate.getUTCHours();
        const endLocalMinutes = endDate.getUTCMinutes();
        const endLocalSeconds = endDate.getUTCSeconds();
        
        const endDateStr = `${endLocalYear}-${String(endLocalMonth).padStart(2, '0')}-${String(endLocalDay).padStart(2, '0')}T${String(endLocalHours).padStart(2, '0')}:${String(endLocalMinutes).padStart(2, '0')}:${String(endLocalSeconds).padStart(2, '0')}`;
        
        const endDayjs = dayjs.tz(endDateStr, displayTimezone);
        endTimeUTC = endDayjs.utc().toISOString();
      }
    } else {
      // UTC timezone - no conversion needed, but ensure it's in UTC format
      startTimeUTC = event.start.toISOString();
      endTimeUTC = event.end ? event.end.toISOString() : null;
    }
    
    // Validate times are in UTC format (ISO string ending with 'Z')
    if (!startTimeUTC || !startTimeUTC.endsWith('Z')) {
      console.error('❌ [CalendarComponent] Start time not in UTC format:', startTimeUTC);
      eventResizeInfo.revert();
      return;
    }
    if (endTimeUTC && !endTimeUTC.endsWith('Z')) {
      console.error('❌ [CalendarComponent] End time not in UTC format:', endTimeUTC);
      eventResizeInfo.revert();
      return;
    }
    
    console.log('🔄 [CalendarComponent] Event resized - converting to UTC:', {
      entryId: entry.id,
      displayTimezone: displayTimezone,
      eventStart: event.start.toISOString(),
      eventEnd: event.end?.toISOString(),
      startTimeUTC,
      endTimeUTC,
      conversion: displayTimezone !== 'UTC' ? {
        from: displayTimezone,
        to: 'UTC',
        localTime: {
          year: event.start.getUTCFullYear(),
          month: event.start.getUTCMonth() + 1,
          day: event.start.getUTCDate(),
          hours: event.start.getUTCHours(),
          minutes: event.start.getUTCMinutes(),
          note: 'These represent local time in display timezone'
        },
        method: 'dayjs.tz().utc()'
      } : 'No conversion needed (UTC)',
      note: 'Converted from header timezone to UTC for API submission'
    });
    
    // Don't refresh the calendar UI - let FullCalendar keep the event where the user resized it
    // Only update the backend data silently via optimistic update

    // If this is a grouped entry, show a confirmation dialog before applying
    const resizedEntryGroupEntries = groupMap ? groupMap.get(String(entry.id)) : null;
    const isGroupEntry = resizedEntryGroupEntries && resizedEntryGroupEntries.length > 1;

    if (isGroupEntry) {
      setGroupDropDialog({
        type: 'resize',
        entry,
        groupEntries: resizedEntryGroupEntries,
        startTimeUTC,
        endTimeUTC,
        revert: eventResizeInfo.revert.bind(eventResizeInfo),
      });
      return;
    }

    // Non-group entry — apply immediately
    try {
      if (onOptimisticUpdate) {
        await onOptimisticUpdate(entry.id, {
          start_time: startTimeUTC,
          end_time: endTimeUTC,
          teamspace: entry.teamspace_id || selectedTeamspaceId,
        });
      }
    } catch (error) {
      console.error('Error updating time entry:', error);
      eventResizeInfo.revert();
    }
  };

  const handleQuickFormSave = async (...args) => {
    let entryId, savedEntry;
    
    if (args.length === 2) {
      [entryId, savedEntry] = args;
    } else {
      [savedEntry] = args;
      entryId = savedEntry.id; 
    }
    
    try {
    if (entryId) {
        // Update existing entry - use optimistic update
      if (onOptimisticUpdate) {
          await onOptimisticUpdate(entryId, savedEntry);
          console.log('✅ [CalendarComponent] Time entry updated successfully (optimistic):', entryId);
          
          // Force FullCalendar to refresh the event to show updated description
          // Use setTimeout to ensure state has propagated through the dependency chain
          setTimeout(() => {
            if (calendarRef.current) {
              const calendarApi = calendarRef.current.getApi();
              const event = calendarApi.getEventById(entryId.toString());
              if (event) {
                // Update the event's extendedProps to reflect the optimistic update
                event.setExtendedProp('entry', { ...event.extendedProps.entry, ...savedEntry });
                // Force a re-render of the event
                calendarApi.render();
              }
            }
          }, 100);
      }
    } else {
        // Create new entry - use optimistic create
      if (onOptimisticCreate) {
          const newEntry = await onOptimisticCreate(savedEntry);
          Object.assign(savedEntry, newEntry);
          console.log('✅ [CalendarComponent] Time entry created successfully (optimistic):', newEntry);
          // No need to refresh - optimistic create already updated the UI
        }
      }
      
      // Only close form on success
      handleQuickFormClose();
        } catch (error) {
      console.error('❌ [CalendarComponent] Error saving time entry:', error);
          console.error('Error details:', error.response?.data || error.message);
          // Don't close the form on error - let the user see the error and retry
          // The QuickTimeEntryForm will show the error message
      // Optimistic handlers will revert the UI changes automatically
          throw error; // Re-throw so QuickTimeEntryForm can handle it
        }
  };

  const handleQuickFormDelete = async (entryId) => {
    try {
    if (onOptimisticDelete) {
        await onOptimisticDelete(entryId);
        console.log('✅ [CalendarComponent] Time entry deleted successfully (optimistic):', entryId);
    }
    
      // Only close form on success
    handleQuickFormClose();
    } catch (error) {
      console.error('❌ [CalendarComponent] Error deleting time entry:', error);
      // Don't close the form on error - let the user see the error
      // Optimistic handler will revert the UI changes automatically
      throw error; // Re-throw so QuickTimeEntryForm can handle it
    }
  };

  const handleQuickFormClose = () => {
    setQuickFormAnchor(null);
    setQuickFormData(null);
    setSelectedTimeSlot(null);
    
    // Clear any calendar selection
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.unselect();
    }
  };

  const handleDetailsEdit = (entry) => {
    // Close details and open quick edit form
    setDetailsOpen(false);
    setQuickFormData({
      ...entry,
      teamspace: entry.teamspace ?? entry.teamspace_id ?? selectedTeamspaceId,
    });
    setSelectedTimeSlot(null);
    setQuickFormAnchor(document.body); // Use document.body as fallback
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedEntryForDetails(null);
  };

  // Get all member options from the members prop — includes inactive members labelled "J Cruz (inactive)"
  const availableMembers = useMemo(() => {
    if (!members || members.length === 0) return [];
    const seen = new Set();
    const options = [];
    for (const member of members) {
      const value = member.username || member.member_username || member.label;
      if (!value || seen.has(value)) continue;
      seen.add(value);
      const inactive = member.is_active === false;
      options.push({ value, label: inactive ? `${value} (inactive)` : value });
    }
    return options.sort((a, b) => a.value.localeCompare(b.value));
  }, [members]);

  // NEW: Get unique teamspaces from the time entries
  const availableTeamspaces = useMemo(() => {
    const uniqueTeamspaces = [...new Set(timeEntries.map(entry => entry.teamspace_id))];
    return uniqueTeamspaces
      .map(teamspaceId => ({
        id: teamspaceId,
        name: teamspaceIdToNameMap[teamspaceId] || `Teamspace ${teamspaceId}`,
        color: teamspaceColorMap[teamspaceId] || '#9E9E9E'
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [timeEntries, teamspaceIdToNameMap, teamspaceColorMap]);

    // Get all clients, sorted alphabetically by code
    const availableClients = useMemo(() => {
      return clients
        .filter(c => !c.is_archived)
        .map(client => ({
          id: client.id,
          code: client.code,
          name: client.name,
          label: `${client.code} - ${client.name}`
        }))
        .sort((a, b) => a.code.localeCompare(b.code));
    }, [clients]);
  
    // Get all categories from categoryLookup, sorted alphabetically, displayed in uppercase
    const availableCategories = useMemo(() => {
      return Object.entries(categoryLookup)
        .map(([id, name]) => ({
          id: `cat:${id}`, // Prefix with "cat:" to distinguish from projects
          name: name,
          label: name ? name.toUpperCase() : name,
          isCategory: true
        }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    }, [categoryLookup]);

    // Get all projects, sorted alphabetically by full project name, and combine with categories (categories first)
    const availableProjects = useMemo(() => {
      const projectList = projects
        .filter(p => !p.is_archived)
        .map(project => {
          const client = clients.find(c => c.id === project.client);
          return {
            id: project.id,
            name: project.name,
            full_project_name: project.full_project_name,
            client_name: client ? client.name : '',
            label: project.full_project_name || project.name,
            isCategory: false
          };
        })
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
      
      // Categories at the top, then projects
      return [...availableCategories, ...projectList];
    }, [projects, clients, availableCategories]);

  // Initialize date range when calendar is ready
  React.useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (calendarApi) {
        updateDateRange(calendarApi);
      }
    }
  }, []);



  const renderEventContent = (eventInfo) => {
    const { event } = eventInfo;
    const projectTitle = event.extendedProps.projectTitle || "No Project";
    const duration = eventInfo.timeText; // e.g., "10:00 - 10:10"
  
    return (
      <div className="event-single-line" title={`${duration} ${projectTitle}`}>
        <strong>{duration}</strong> {projectTitle}
      </div>
    );
  };

  // Don't render calendar until timezone is ready
  if (!timezoneReady || !validatedTimezone) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body1" sx={{ color: 'secondary.main' }}>
          Loading timezone...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Filters Section */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        gap: 1.5, 
        padding: '12px', // Reduced padding
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: '12px', // Reduced margin
        alignItems: 'center',
        backgroundColor: calBg,
        borderRadius: '12px 12px 0 0', // Rounded top corners
      }}>
      {/* Calendar Title */}
      <Typography
        sx={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: calTextMain,
          marginRight: 'auto', // Push filters to the right
          minWidth: 'fit-content'
        }} 
        variant="h5" 
        component="h1"
      >
        Calendar
      </Typography>

      {isPhone && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setPhoneFiltersOpen((open) => !open)}
          aria-expanded={phoneFiltersOpen}
          sx={{ borderRadius: '8px', color: calTextMain, borderColor: theme.palette.editorial?.borderStrong || calGrid }}
        >
          {(() => {
            const activeCount = selectedClients.length + selectedProjects.length + selectedTeamspaces.length
              + (selectedBillable !== 'all' ? 1 : 0)
              + (selectedMember !== (user?.username || user?.email || '') ? 1 : 0);
            return activeCount ? `Filters (${activeCount})` : 'Filters';
          })()}
        </Button>
      )}

      <PhoneFilterCollapse isPhone={isPhone} open={phoneFiltersOpen}>
      {/* Client Filter */}
      <Autocomplete
        multiple
        options={availableClients}
        loading={isMembersLoading}
        value={availableClients.filter(client => selectedClients.includes(client.code))}
        onChange={(event, newValue) => {
          setSelectedClients(newValue.map(client => client.code));
        }}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Client"
            size="small"
              sx={{
              minWidth: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.8rem',
              }
            }}
          />
        )}
        renderTags={(selected, getTagProps) =>
          selected.length === 0 ? (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }} color="text.secondary">All Clients</Typography>
          ) : (
            `${selected.length} selected`
          )
        }
        disableCloseOnSelect
        size="small"
        sx={{ minWidth: 150 }}
      />

      {/* Project Filter (includes categories) */}
      <Autocomplete
        multiple
        options={availableProjects}
        value={availableProjects.filter(item => selectedProjects.includes(item.id))}
        onChange={(event, newValue) => {
          setSelectedProjects(newValue.map(item => item.id));
        }}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Project"
            size="small"
              sx={{
              minWidth: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.8rem',
              }
            }}
          />
        )}
        renderTags={(selected, getTagProps) =>
          selected.length === 0 ? (
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }} color="text.secondary">All Projects</Typography>
          ) : (
            `${selected.length} selected`
          )
        }
        disableCloseOnSelect
        size="small"
        sx={{ minWidth: 150 }}
      />

        {/* Billable Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="billable-select-label">Billable</InputLabel>
          <Select
            labelId="billable-select-label"
            value={selectedBillable}
            onChange={(e) => setSelectedBillable(e.target.value)}
            input={<OutlinedInput label="Billable" />}
            sx={{
              borderRadius: '8px', // Rounded corners
              '& .MuiOutlinedInput-root': {
                fontSize: '0.8rem', // Reduced text
              }
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="billable">Billable</MenuItem>
            <MenuItem value="non-billable">Non-Billable</MenuItem>
          </Select>
        </FormControl>

        {/* Teamspace Filter */}
        <Autocomplete
          multiple
          options={availableTeamspaces}
          value={availableTeamspaces.filter(teamspace => selectedTeamspaces.includes(teamspace.id))}
          onChange={(event, newValue) => {
            setSelectedTeamspaces(newValue.map(teamspace => teamspace.id));
          }}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Teamspace"
              size="small"
              sx={{
              minWidth: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.8rem',
              }
            }}
            />
          )}
          renderTags={(selected, getTagProps) =>
            selected.length === 0 ? (
              <Typography variant="body2" color="text.secondary">All Teamspaces</Typography>
            ) : (
              `${selected.length} selected`
            )
          }
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box 
                sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  backgroundColor: option.color,
                  flexShrink: 0 
                }} 
              />
              <Typography sx={{ fontSize: '0.8rem' }}>{option.name}</Typography>
            </Box>
          )}
          disableCloseOnSelect
          size="small"
          sx={{ minWidth: 150 }}
        />

        {/* Member Filter */}
        <FormControl size="small" sx={{ minWidth: 150, position: 'relative' }}>
          <InputLabel id="member-select-label">Member</InputLabel>
          <Select
            labelId="member-select-label"
            value={selectedMember}
            onChange={(e) => setSelectedMember(e.target.value)}
            input={<OutlinedInput label="Member" />}
            disabled={loadingAllMembers}
            sx={{
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                fontSize: '0.9rem',
              }
            }}
          >
            {availableMembers.map(member => (
              <MenuItem key={member.value} value={member.value}>{member.label}</MenuItem>
            ))}
          </Select>
          {/* Loading overlay for member filter */}
          {loadingAllMembers && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? 'rgba(26,26,26,0.8)' : 'rgba(255,255,255,0.7)',
                borderRadius: '8px',
                zIndex: 10,
                backdropFilter: 'blur(2px)',
              }}
            >
              <CircularProgress size={20} />
            </Box>
          )}
        </FormControl>

        {/* Clear Filters Button */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleClearFilters}
          startIcon={<ClearIcon />}
          sx={{ 
            minWidth: 130,
            borderRadius: '8px',
            fontSize: '0.9rem',
            px: 2,
            color: calText,
            borderColor: theme.palette.editorial?.borderStrong || calGrid,
            '&:hover': {
              borderColor: calTextMain,
              backgroundColor: theme.palette.editorial?.bgHover,
            },
          }}
        >
          Clear Filters
        </Button>
      </PhoneFilterCollapse>

        {/* Active Filters Display */}
        {(selectedClients.length > 0 || selectedProjects.length > 0 || selectedBillable !== 'all' || selectedMember !== (user?.username || user?.email || '')) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {selectedClients.map(client => (
              <Chip 
                key={client} 
                label={`Client: ${client}`} 
                size="small" 
                onDelete={() => setSelectedClients(prev => prev.filter(c => c !== client))}
                sx={{
                  fontSize: '0.7rem', // Reduced chip text
                  height: '24px', // Reduced height
                }}
              />
            ))}
            {selectedProjects.map(itemId => {
              const idStr = String(itemId);
              // Check if it's a category (prefixed with "cat:")
              if (idStr.startsWith('cat:')) {
                const categoryId = idStr.replace('cat:', '');
                const categoryName = categoryLookup[categoryId];
                return (
                  <Chip 
                    key={itemId} 
                    label={`Project: ${categoryName || categoryId}`} 
                    size="small" 
                    onDelete={() => setSelectedProjects(prev => prev.filter(p => String(p) !== idStr))}
                    sx={{
                      fontSize: '0.8rem', // Larger chip text
                      height: '28px', // Slightly taller
                    }}
                  />
                );
              } else {
                // It's a project
                const project = projects.find(p => p.id === itemId);
                return (
                  <Chip 
                    key={itemId} 
                    label={`Project: ${project?.full_project_name || project?.name || itemId}`} 
                    size="small" 
                    onDelete={() => setSelectedProjects(prev => prev.filter(p => p !== itemId))}
                    sx={{
                      fontSize: '0.8rem', // Larger chip text
                      height: '28px', // Slightly taller
                    }}
                  />
                );
              }
            })}
            {selectedTeamspaces.map(teamspaceId => {
              const teamspace = availableTeamspaces.find(t => t.id === teamspaceId);
              return (
                <Chip 
                  key={teamspaceId} 
                  label={`Teamspace: ${teamspace?.name || teamspaceId}`} 
                  size="small" 
                  onDelete={() => setSelectedTeamspaces(prev => prev.filter(t => t !== teamspaceId))}
                  sx={{
                    fontSize: '0.8rem',
                    height: '28px',
                  }}
                />
              );
            })}
            {selectedBillable !== 'all' && (
            <Chip 
              label={`Billable: ${selectedBillable}`} 
              size="small" 
              onDelete={() => setSelectedBillable('all')}
              sx={{
                fontSize: '0.8rem', // Larger chip text
                height: '28px', // Slightly taller
              }}
            />
          )}
          {selectedMember !== (user?.username || user?.email || '') && (
            <Chip 
              label={`Member: ${selectedMember}`} 
              size="small" 
              onDelete={() => setSelectedMember(user?.username || user?.email || '')}
              sx={{
                fontSize: '0.8rem', // Larger chip text
                height: '28px', // Slightly taller
              }}
            />
          )}
          </Box>
        )}
          
        
      </Box>
      
      {/* Calendar Controls */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        // Phones: tighter, and the legend wraps onto its own line.
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        rowGap: { xs: 1, sm: 0 },
        padding: { xs: '8px 4px', sm: '12px 16px' }, // Adjusted padding
        marginBottom: '8px', // Reduced margin
        backgroundColor: calBg,
        // Sideways phones render the phone switcher too, so they wrap as well.
        [PHONE_MEDIA]: { flexWrap: 'wrap', rowGap: 1 },
      }}>
        {/* Today Button - Left Side */}
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleToday}
            sx={{
              borderRadius: '8px',
              fontSize: '0.8rem',
              px: 2,
              color: calTextMain,
              borderColor: theme.palette.editorial?.borderStrong || calGrid,
              '&:hover': {
                borderColor: calTextMain,
                backgroundColor: theme.palette.editorial?.bgHover,
              },
            }}
          >
            Today
          </Button>
        </Box>

        {/* Phones: view switcher (Day / 3 Days / Week) */}
        {isPhone && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={currentView}
            onChange={(e, view) => { if (view) handleViewChange(view); }}
            sx={{ order: 3, width: '100%', '& .MuiToggleButton-root': { flex: 1, py: 0.5, fontSize: '0.75rem' } }}
          >
            {PHONE_VIEW_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        {/* Date Range Display with Navigation Buttons - Center */}
        {currentDateRange.start && currentDateRange.end && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, sm: 2 },
            flex: 1,
            margin: { xs: '0 4px', sm: '0 16px' }
          }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePrev}
              sx={{
                borderRadius: '8px',
                minWidth: '40px',
                fontSize: '0.9rem',
                color: calTextMain,
                borderColor: theme.palette.editorial?.borderStrong || calGrid,
                '&:hover': {
                  borderColor: calTextMain,
                  backgroundColor: theme.palette.editorial?.bgHover,
                },
              }}
            >
              ‹
            </Button>
            <Typography variant="h6" sx={{
              color: calTextMain,
              fontSize: '0.95rem',
              fontWeight: 500,
              minWidth: 'fit-content'
            }}>
              {isPhone
                ? (currentView === 'timeGridDay'
                    ? format(currentDateRange.start, 'EEE, MMM d')
                    : `${format(currentDateRange.start, 'MMM d')} – ${format(subDays(currentDateRange.end, 1), 'MMM d')}`)
                : <>{format(currentDateRange.start, 'MMM d, yyyy')} - {format(subDays(currentDateRange.end, 1), 'MMM d, yyyy')}</>}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleNext}
              sx={{
                borderRadius: '8px',
                minWidth: '40px',
                fontSize: '0.9rem',
                color: calTextMain,
                borderColor: theme.palette.editorial?.borderStrong || calGrid,
                '&:hover': {
                  borderColor: calTextMain,
                  backgroundColor: theme.palette.editorial?.bgHover,
                },
              }}
            >
              ›
            </Button>
          </Box>
        )}

        {/* Teamspace Color Legend - Right Side */}
        {teamspaceLegend.length > 1 && (
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            alignItems: 'center',
            order: { xs: 4, sm: 0 },
          }}>
            <Typography variant="body2" sx={{ color: calTextMain, fontWeight: 'bold', mr: 1, fontSize: '0.75rem' }}>
              Teamspaces:
            </Typography>
            {teamspaceLegend.map(teamspace => (
              <Box key={teamspace.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: teamspace.color,
                    border: `1px solid ${calGrid}`,
                    flexShrink: 0
                  }}
                />
                <Typography variant="body2" sx={{ color: calTextMain, fontSize: '0.75rem' }}>
                  {teamspace.name}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Calendar */}
      <CalendarGridStyles
        calBg={calBg} calGrid={calGrid} calGridSub={calGridSub}
        calGridFaint={calGridFaint} calToday={calToday} calText={calText}
      />
      <Box className="gva-calendar" sx={{ 
        position: 'relative',
        flex: 1,
        minHeight: 0,
        // Sideways phone: the page scrolls (CalendarPage) and the grid gets a full
        // screen height of its own instead of the sliver left under the toolbar.
        [SHORT_TOUCH_MEDIA]: { flex: 'none', height: 'calc(100dvh - 56px)' },
        borderRadius: 0, 
        overflow: 'auto', 
        boxShadow: 'none',
        backgroundColor: calBg,
      }}>
        <CalendarPresenceMarkers labelsRef={presenceLabelsRef} />
        <FullCalendar
          key={`calendar-${validatedTimezone || 'UTC'}-${timezoneReady ? 'ready' : 'not-ready'}`}
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          slotEventOverlap={false}
          headerToolbar={false}
          // Phones open on today when it is in the shown week (Day view), else its Monday.
          initialDate={isPhone ? phoneInitialDate : currentWeekStart}
          initialView={currentView}
          views={CALENDAR_VIEWS}
          // eventContent={renderEventContent}
          datesSet={handleDatesSet}
          events={calendarEvents}
          height="100%"
          firstDay={1}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="01:00:00"
          slotLabelInterval="01:00:00"
          snapDuration="00:15:00" 
          timeZone={validatedTimezone}
          slotLabelFormat={{
            hour: 'numeric',
            meridiem: 'short',
            hour12: true
          }}
          eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false
          }}
          displayEventTime={true} 
          dayHeaderFormat={{ weekday: 'short', day: 'numeric',}}
          selectable={true}
          editable={!isPhone} // Enable editing (drag/resize), but we'll control per event; phones edit via the form
          selectMirror={true}
          allDaySlot={false}
          eventDisplay="block"
          dayMaxEvents={false}
          moreLinkClick="popover"
          eventOrder="start,-duration,allDay,title"
          nowIndicator={true}
          nowIndicatorClassNames="fc-now-indicator"
          select={handleTimeSlotSelect}
          eventMinWidth={0}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventContent={renderEventHtml}
          eventDidMount={handleEventDidMount}
          {...(isPhone ? { dateClick: handlePhoneDateClick, longPressDelay: PHONE_LONG_PRESS_MS } : {})}
        />
        
        {/* Loading Overlay - Only covers calendar area */}
        {timeEntriesLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10, // Lower z-index to only cover calendar content, not sidebar/floating button
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(43, 42, 43, 0.7)',
              borderRadius: '0 0 12px 12px', // Match calendar border radius
              pointerEvents: 'none', // Allow clicks to pass through to elements below (though calendar is covered)
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pointerEvents: 'auto' }}>
              <CircularProgress sx={{ color: '#ffffff' }} size={60} thickness={4} />
              <Typography variant="h6" sx={{ color: '#ffffff' }}>
                Loading calendar...
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Quick Time Entry Form */}
      <QuickTimeEntryForm
        open={!!quickFormAnchor}
        anchorEl={quickFormAnchor}
        onClose={handleQuickFormClose}
        initialData={quickFormData}
        selectedTimeSlot={selectedTimeSlot}
        onSave={handleQuickFormSave}
        onDelete={handleQuickFormDelete}
        groupEntries={groupMap && quickFormData?.id ? (groupMap.get(String(quickFormData.id)) || null) : null}
        filterSelectedMember={canCreateForOthers ? selectedMember : null}
      />

      {/* Edit Time Entry Form (for existing calendar entries) */}
      <EditTimeEntryForm
        open={editModalOpen}
        handleClose={() => { setEditModalOpen(false); setEditModalEntry(null); }}
        initialData={editModalEntry}
        groupEntries={groupMap && editModalEntry?.id ? (groupMap.get(String(editModalEntry.id)) || null) : null}
        onTimeEntryUpdated={onTimeEntryUpdated}
        onTimeEntryAdded={onTimeEntryAdded}
        onTimeEntryDeleted={onTimeEntryDeleted}
      />

      {/* Time Entry Details Dialog */}
      <TimeEntryDetails
        open={detailsOpen}
        onClose={handleDetailsClose}
        timeEntry={selectedEntryForDetails}
        projectLookup={projectLookupForDetails}
        clientLookup={clientLookupForDetails}
        onEdit={selectedEntryForDetails && canEditEntry(selectedEntryForDetails) ? handleDetailsEdit : undefined}
        displayTimezone={currentTimezone || 'UTC'}
      />

      {/* Group drag/resize confirmation dialog */}
      <Dialog open={!!groupDropDialog} onClose={() => { groupDropDialog?.revert(); setGroupDropDialog(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>{groupDropDialog?.type === 'resize' ? 'Resize Grouped Time Entry' : 'Move Grouped Time Entry'}</DialogTitle>
        <DialogContent>
          <Typography>
            This time entry is part of a group ({groupDropDialog?.groupEntries?.length} members). Do you want to {groupDropDialog?.type === 'resize' ? 'resize' : 'move'} only this entry or all entries in the group?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { groupDropDialog?.revert(); setGroupDropDialog(null); }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={async () => {
              const { entry, startTimeUTC, endTimeUTC } = groupDropDialog;
              setGroupDropDialog(null);
              try {
                if (onOptimisticUpdate) {
                  await onOptimisticUpdate(entry.id, {
                    start_time: startTimeUTC,
                    end_time: endTimeUTC,
                    teamspace: entry.teamspace_id || selectedTeamspaceId,
                  });
                }
              } catch (error) {
                console.error('Error updating time entry:', error);
              }
            }}
          >
            This entry only
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const { entry, groupEntries, startTimeUTC, endTimeUTC } = groupDropDialog;
              setGroupDropDialog(null);
              try {
                if (onOptimisticUpdate) {
                  for (const groupEntry of groupEntries) {
                    await onOptimisticUpdate(groupEntry.id, {
                      start_time: startTimeUTC,
                      end_time: endTimeUTC,
                      teamspace: groupEntry.teamspace_id || groupEntry.teamspace || entry.teamspace_id || selectedTeamspaceId,
                    });
                  }
                }
              } catch (error) {
                console.error('Error updating group time entries:', error);
              }
            }}
          >
            All members
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CalendarComponent;